import type { CommunicationServiceManagementClient } from "@azure/arm-communication";

const MANAGED_DOMAIN = "AzureManagedDomain";

export interface AcsProvisionResult {
  endpoint: string;
  sender: string;
}

interface ProvisionArgs {
  client: CommunicationServiceManagementClient;
  subscriptionId: string;
  resourceGroup: string;
  name: string; // base name; ACS + email-service names derive from it unless overridden below
  dataLocation: string; // e.g. "United States"
  /** Explicit ACS resource name, bypassing the `acs-${name}` derivation. Callers that
   *  want a rename-stable name pass this — deriving from an agent name is what let the
   *  ezra rename orphan a working ACS. */
  acsName?: string;
  /** Explicit Email Communication Service name, bypassing the `${name}-email` derivation. */
  emailName?: string;
  /** Tags stamped on both resources, so a later discovery pass can recognise the one this
   *  platform provisioned for itself. */
  tags?: Record<string, string>;
  onProgress?: (m: string) => void;
}

/**
 * Idempotent create-or-update: ACS resource, Email Communication Service, Azure-managed
 * domain, then link the domain to the ACS resource. Returns the data-plane endpoint and
 * the managed DoNotReply sender address.
 *
 * The ACS resource is provisioned inside the Contributor-granted resource group so the
 * executor MI's existing Contributor grant authorises the data-plane send — no extra
 * role assignment is needed.
 */
export async function provisionAcsEmail(args: ProvisionArgs): Promise<AcsProvisionResult> {
  const { client, subscriptionId, resourceGroup, name, dataLocation, tags, onProgress } = args;
  const acsName = args.acsName ?? `acs-${name}`;
  const emailName = args.emailName ?? `${name}-email`;

  onProgress?.(`creating ACS resource ${acsName}…`);
  const acs = await client.communicationServices.beginCreateOrUpdateAndWait(
    resourceGroup,
    acsName,
    { location: "global", dataLocation, ...(tags ? { tags } : {}) },
  );

  onProgress?.(`creating Email Communication Service ${emailName}…`);
  await client.emailServices.beginCreateOrUpdateAndWait(resourceGroup, emailName, {
    location: "global",
    dataLocation,
    ...(tags ? { tags } : {}),
  });

  onProgress?.("provisioning the Azure-managed domain…");
  const domain = await client.domains.beginCreateOrUpdateAndWait(
    resourceGroup,
    emailName,
    MANAGED_DOMAIN,
    { location: "global", domainManagement: "AzureManaged" },
  );

  const domainResourceId =
    `/subscriptions/${subscriptionId}/resourceGroups/${resourceGroup}` +
    `/providers/Microsoft.Communication/emailServices/${emailName}/domains/${MANAGED_DOMAIN}`;

  onProgress?.("linking the domain to the ACS resource…");
  // A second create-or-update is a full PUT, so the tags set on the first call have to be
  // repeated here or the link would strip them — and discovery reads those tags.
  await client.communicationServices.beginCreateOrUpdateAndWait(resourceGroup, acsName, {
    location: "global",
    dataLocation,
    linkedDomains: [domainResourceId],
    ...(tags ? { tags } : {}),
  });

  const hostName = acs.hostName ?? `${acsName}.communication.azure.com`;
  const fromSenderDomain = domain.fromSenderDomain;
  if (!fromSenderDomain) {
    throw new Error("ACS managed domain did not report a fromSenderDomain");
  }
  return {
    endpoint: `https://${hostName}/`,
    sender: `DoNotReply@${fromSenderDomain}`,
  };
}
